import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Camera, User } from 'lucide-react';
import axios from 'axios';

const AuthPage = () => {
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    first_name: '', 
    last_name: '', 
    email: '', 
    contact_number: '', 
    physical_address: '', 
    password: '',
    profile_photo: '',
    referral_code: '',
    referred_by_name: ''
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [banner, setBanner] = useState({ enabled: false, text: '' });
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Pull the configurable sign-in banner text + toggle from public site
    // content. Falls back silently if the endpoint is unreachable.
    axios.get(`${API_URL}/api/site-content`)
      .then((r) => {
        setBanner({
          enabled: String(r.data?.auth_banner_enabled || '').toLowerCase() === 'true',
          text: r.data?.auth_banner_text || '',
        });
      })
      .catch(() => {});
  }, [API_URL]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      toast.success('Temporary password sent! Check your email and use it to log in, then change your password in Dashboard.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Login successful!');
      navigate(from);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Check for referral code: form field > localStorage > URL param
      let referralCode = registerData.referral_code?.trim() || null;
      if (!referralCode) {
        const storedCode = localStorage.getItem('referral_code');
        const expiry = localStorage.getItem('referral_expiry');
        if (storedCode && expiry && Date.now() < parseInt(expiry)) {
          referralCode = storedCode;
        }
      }
      if (!referralCode) {
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.get('ref')) {
          referralCode = urlParams.get('ref');
        }
      }

      await register(
        registerData.first_name,
        registerData.last_name,
        registerData.email,
        registerData.contact_number,
        registerData.physical_address,
        registerData.password,
        registerData.profile_photo,
        referralCode,
        registerData.referred_by_name?.trim() || null
      );
      toast.success('Registration successful!');
      // Clean up referral code from localStorage after use
      if (referralCode) {
        localStorage.removeItem('referral_code');
        localStorage.removeItem('referral_expiry');
        localStorage.removeItem('referral_source');
      }
      navigate(from);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Photo must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setRegisterData({ ...registerData, profile_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] py-12">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_discount-tiers-1/artifacts/gus3g039_JustDive.co.za%20LOGO.png" 
              alt="Just Dive Logo" 
              className="h-20 w-auto object-contain mx-auto"
              data-testid="auth-logo"
            />
          </Link>
          <h1 className="text-3xl font-heading font-bold text-[#0A2342] mb-2">
            Welcome to Just Dive
          </h1>
          <p className="text-gray-600">Sign in or create an account to continue</p>
        </div>

        <div className="bg-white p-8 rounded-lg border border-gray-100 shadow-lg">
          {showForgotPassword ? (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="text-xl font-heading font-bold text-[#0A2342]">
                  Reset Password
                </h2>
              </div>
              <p className="text-gray-600 text-sm">
                Enter your email address and we'll send you a temporary password. You can then log in and set a new password.
              </p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email">Email Address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="your@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="mt-2"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Temporary Password'
                  )}
                </Button>
              </form>
            </div>
          ) : (
          <Tabs defaultValue={(() => {
            const qs = new URLSearchParams(location.search);
            return (qs.get('signup') === 'true' || qs.get('mode') === 'register') ? 'register' : 'login';
          })()} className="w-full">
            {/* Sign-in banner — fully admin-controlled. Edit text and toggle
                visibility from Admin → Settings → Sign-in Banner. */}
            {banner.enabled && banner.text && (
              <div className="mb-4 p-3 bg-[#FFF3ED] border border-[#FF6B35]/20 rounded-lg text-center" data-testid="auth-banner">
                <p className="text-sm text-[#FF6B35] font-semibold">{banner.text}</p>
              </div>
            )}
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="your@email.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0A2342] text-white hover:bg-[#0A2342]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-[#FF6B35] hover:text-[#FF6B35]/80 hover:underline transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Friction-cut signup form: ask only for the essentials.
                    Shipping address is collected at first checkout where it's
                    actually needed. Contact number stays here — it's critical
                    for reaching the customer about orders/trips. */}
                <p className="text-xs text-gray-500 text-center -mt-1 mb-1">
                  Just 5 quick fields — you'll add your shipping address at checkout if you ever order gear.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="register-first-name">First Name</Label>
                    <Input
                      id="register-first-name"
                      type="text"
                      placeholder="John"
                      value={registerData.first_name}
                      onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                      required
                      className="mt-2"
                      data-testid="register-first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-last-name">Last Name</Label>
                    <Input
                      id="register-last-name"
                      type="text"
                      placeholder="Doe"
                      value={registerData.last_name}
                      onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                      required
                      className="mt-2"
                      data-testid="register-last-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-email">Email</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="your@email.com"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-contact">Contact Number</Label>
                  <Input
                    id="register-contact"
                    type="tel"
                    placeholder="0821234567"
                    value={registerData.contact_number}
                    onChange={(e) => setRegisterData({ ...registerData, contact_number: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="register-contact-input"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">Password</Label>
                  <Input
                    id="register-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="register-password-input"
                  />
                </div>
                {/* Referral Section */}
                <div className="border-t pt-4 mt-2">
                  <p className="text-sm text-gray-500 mb-3">Were you referred? (optional)</p>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label htmlFor="register-referral-code">Referral Code</Label>
                      <Input
                        id="register-referral-code"
                        placeholder="e.g. JD-SARAH-1234 or SUMMER-DIVE"
                        value={registerData.referral_code}
                        onChange={(e) => setRegisterData({ ...registerData, referral_code: e.target.value })}
                        className="mt-1"
                        data-testid="register-referral-code-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="register-referred-by">Or referred by (name)</Label>
                      <Input
                        id="register-referred-by"
                        placeholder="Name of who referred you"
                        value={registerData.referred_by_name}
                        onChange={(e) => setRegisterData({ ...registerData, referred_by_name: e.target.value })}
                        className="mt-1"
                        data-testid="register-referred-by-input"
                      />
                    </div>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;